import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import * as authApi from './authApi.js';
import { setAccessToken, setRefreshHandler } from './authBridge.js';

export interface AuthUser {
  readonly id: string;
  readonly username: string;
  readonly displayName: string;
  readonly roles: readonly string[];
}

export interface AuthState {
  readonly user: AuthUser | null;
  readonly token: string | null;
  readonly refreshToken: string | null;
  readonly mfaChallengeId: string | null;
  readonly mfaMethod: string | null;
}

export interface AuthContextValue extends AuthState {
  readonly login: (username: string, password: string, tenantCode?: string) => Promise<{ requiresMfa: boolean }>;
  readonly verifyMfa: (code: string) => Promise<void>;
  readonly logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY = 'tradelogic.vendor.auth.v1';

/**
 * SEC-001 — Only NON-SENSITIVE user metadata is persisted.
 * Tokens live in memory (access JWT in authBridge) or in the httpOnly
 * `refresh_token` cookie set by the backend.
 */
interface PersistedAuth {
  readonly user: AuthUser | null;
  readonly mfaChallengeId: string | null;
  readonly mfaMethod: string | null;
}

function readStored(): PersistedAuth | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedAuth & AuthState>;
    return {
      user: parsed.user ?? null,
      mfaChallengeId: parsed.mfaChallengeId ?? null,
      mfaMethod: parsed.mfaMethod ?? null,
    };
  } catch {
    return null;
  }
}

function writeStored(state: AuthState): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (!state.user && !state.mfaChallengeId) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      // SEC-001: tokens NEVER hit storage.
      const persisted: PersistedAuth = {
        user: state.user,
        mfaChallengeId: state.mfaChallengeId,
        mfaMethod: state.mfaMethod,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
    }
  } catch { /* ignore quota / disabled storage */ }
}

export interface AuthProviderProps {
  readonly children: ReactNode;
  readonly initialState?: Partial<AuthState>;
}

export function AuthProvider({ children, initialState }: AuthProviderProps): ReactNode {
  const stored = initialState ? null : readStored();
  const [state, setState] = useState<AuthState>({
    user: initialState?.user ?? stored?.user ?? null,
    // SEC-001: tokens never persisted — recovered via 401-refresh.
    token: initialState?.token ?? null,
    refreshToken: initialState?.refreshToken ?? null,
    mfaChallengeId: initialState?.mfaChallengeId ?? stored?.mfaChallengeId ?? null,
    mfaMethod: initialState?.mfaMethod ?? stored?.mfaMethod ?? null,
  });

  useEffect(() => { writeStored(state); }, [state]);

  // INT-012: keep api-client bridge in sync.
  useEffect(() => { setAccessToken(state.token); }, [state.token]);
  useEffect(() => {
    // SEC-001: refresh token rides in the httpOnly cookie attached by
    // the browser; the handler takes no args.
    const handler = async (): Promise<string | null> => {
      const res = await authApi.refresh();
      if (!res) {
        setState({ user: null, token: null, refreshToken: null, mfaChallengeId: null, mfaMethod: null });
        return null;
      }
      setState((s) => ({
        ...s,
        token: res.token,
        refreshToken: null,
        user: res.user ?? s.user,
      }));
      return res.token;
    };
    setRefreshHandler(handler);
    return () => { setRefreshHandler(null); };
  }, []);

  // [DEV-BYPASS] See StartBMS.bat. Production builds never enter this branch.
  const login = useCallback(async (username: string, password: string, tenantCode?: string) => {
    const devEnv = Boolean((import.meta as { env?: { DEV?: boolean } }).env?.DEV);
    const devUsers: Record<string, readonly string[]> = {
      admin: ['VENDOR_ADMIN'],
      vendoradmin: ['VENDOR_ADMIN'],
      superadmin: ['SUPER_ADMIN', 'VENDOR_ADMIN'],
    };
    const uname = username.trim().toLowerCase();
    if (devEnv && password === 'a' && devUsers[uname]) {
      setState({
        user: {
          id: `dev-${uname}`,
          username: uname,
          displayName: uname.charAt(0).toUpperCase() + uname.slice(1) + ' (DEV)',
          roles: devUsers[uname],
        },
        token: `dev-token-${uname}`,
        refreshToken: null,
        mfaChallengeId: null,
        mfaMethod: null,
      });
      return { requiresMfa: false };
    }

    const res = await authApi.login(username, password, tenantCode);
    if (res.requiresMfa) {
      setState((s) => ({
        ...s,
        mfaChallengeId: res.mfaChallengeId,
        mfaMethod: res.mfaMethod,
      }));
      return { requiresMfa: true };
    }
    setState({
      user: res.user,
      token: res.token,
      refreshToken: null, // SEC-001: httpOnly cookie
      mfaChallengeId: null,
      mfaMethod: null,
    });
    return { requiresMfa: false };
  }, []);

  const verifyMfa = useCallback(
    async (code: string) => {
      const challengeId = state.mfaChallengeId;
      if (!challengeId) throw new Error('No MFA challenge in progress');
      const res = await authApi.verifyMfa(challengeId, code, state.mfaMethod);
      setState({
        user: res.user,
        token: res.token,
        refreshToken: null, // SEC-001: httpOnly cookie
        mfaChallengeId: null,
        mfaMethod: null,
      });
    },
    [state.mfaChallengeId, state.mfaMethod],
  );

  const logout = useCallback(() => {
    void authApi.logout();
    setState({ user: null, token: null, refreshToken: null, mfaChallengeId: null, mfaMethod: null });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, verifyMfa, logout }),
    [state, login, verifyMfa, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

